import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { http } from "@/lib/api";
import {
  Shield, Flower, MapPin, Loader2, ArrowRight, Feather, Sparkles,
  Users, Award, Flag,
} from "lucide-react";
import ObituariesTabs from "@/components/ObituariesTabs";
import ArticlesOfInterest from "@/components/ArticlesOfInterest";
import SeoHead from "@/components/SeoHead";
import MemorialBadges from "@/components/MemorialBadges";

// -----------------------------------------------------------------------------
// /obituaries/category/:category — a landing page for one memorial category
// (Veterans / Military, Healthcare Workers, Teachers, Musicians, etc.).
// Renders a themed hero + a curated_tag-filtered grid of every memorial in
// that category. Site-agnostic — the same page renders unchanged on
// Social Pulse, myfamilytree.care, and indianfamilytree.com.
// -----------------------------------------------------------------------------

// Category slug → curated_tags to match, hero copy, icon, colour theme.
// Only Veterans is fully fleshed out for now — the schema is generic so
// adding Healthcare Workers / Teachers / Scientists later is a one-line
// change here (backend curated_tags are already free-form).
const CATEGORIES = {
  veterans: {
    label: "Veterans & Military",
    tags: ["veteran", "military"],
    // Article tags used to fetch "Articles of Interest" for this category
    // via /api/sp-articles/by-tag/{tag}. Any tag with at least one
    // published article will populate the strip.
    article_tags: ["veterans", "military", "armed-forces", "war-history"],
    tagMatch: "any", // any of the tags matches
    icon: Shield,
    accent: "#7C3AED",
    eyebrow: "IN LOVING MEMORY",
    title: (
      <>
        Veterans and Military <br />
        <span style={{ color: "#7C3AED" }}>remembered forever.</span>
      </>
    ),
    lead: (
      "Soldiers, sailors, air-crew and officers who served their nations " +
      "— captured here as one enduring tribute. Every rank, every regiment, " +
      "every family that carries their loss. Add someone you loved so their " +
      "story is never forgotten."
    ),
    facts: [
      { icon: Flag,   label: "Every nation" },
      { icon: Award,  label: "Every rank" },
      { icon: Users,  label: "Every family" },
    ],
  },
  military: { alias: "veterans" },
  "war-heroes": { alias: "veterans" },
};


export default function ObituariesCategory() {
  const { category } = useParams();
  const cfg = useMemo(() => {
    const key = (category || "").toLowerCase();
    const conf = CATEGORIES[key];
    if (!conf) return null;
    if (conf.alias) return CATEGORIES[conf.alias];
    return conf;
  }, [category]);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!cfg) return;
    setLoading(true);
    // Curated-tag filter: the backend `?tag=` param does an $all match,
    // so multiple tags are AND-ed together (a memorial must carry every
    // listed tag). For an "OR" behaviour we fetch each tag then merge.
    const fetches = cfg.tagMatch === "any"
      ? cfg.tags.map((t) => http.get("/memorials", { params: { tag: t, limit: 200 } }))
      : [http.get("/memorials", { params: { tag: cfg.tags.join(","), limit: 200 } })];

    Promise.all(fetches)
      .then((rs) => {
        const seen = new Set();
        const merged = [];
        for (const r of rs) {
          for (const m of (r.data?.items || [])) {
            if (!seen.has(m.id)) {
              seen.add(m.id);
              merged.push(m);
            }
          }
        }
        setItems(merged);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [cfg]);

  if (!cfg) {
    return (
      <div className="max-w-3xl mx-auto py-24 px-6 text-center" data-testid="obit-category-not-found">
        <Flower className="w-10 h-10 opacity-40 mx-auto" />
        <h1 className="font-display text-3xl mt-4">Category not found</h1>
        <p className="mt-2" style={{ color: "var(--wofy-text-soft)" }}>
          The memorial category "{category}" doesn't exist yet.
        </p>
        <div className="mt-6">
          <Link to="/obituaries/hub" className="wofy-btn-primary inline-flex items-center gap-2">
            Back to obituaries hub <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }

  const Icon = cfg.icon;

  return (
    <div data-testid="obituaries-category-page" className="max-w-[1400px] mx-auto px-6 lg:px-10 py-14">
      <SeoHead
        section="obituaries"
        title={`${cfg.label} Memorials | SocialPulse — Honoring Lives, Preserving Memories Forever`}
        description={`${cfg.label} remembered on SocialPulse — every rank, every family, every story. ${cfg.lead || ""}`}
        url={`https://socialpulse.com/obituaries/category/${(category || "").toLowerCase()}`}
      />
      <ObituariesTabs position="top" />

      {/* Hero -------------------------------------------------------- */}
      <section className="grid lg:grid-cols-[1.3fr_1fr] gap-10 items-center pt-4">
        <div>
          <div
            className="inline-flex items-center gap-2 rounded-full px-3 py-1 mb-4"
            style={{ background: `${cfg.accent}18`, color: cfg.accent }}
            data-testid="category-eyebrow"
          >
            <Icon className="w-4 h-4" />
            <span className="text-[10px] font-mono uppercase tracking-widest">
              {cfg.eyebrow}
            </span>
          </div>
          <h1 className="font-display text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
            {cfg.title}
          </h1>
          <p className="mt-5 text-base md:text-lg max-w-2xl leading-relaxed" style={{ color: "var(--wofy-text-soft)" }}>
            {cfg.lead}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/obituaries/submit"
              data-testid="category-cta-create"
              className="wofy-btn-primary inline-flex items-center gap-2 !text-base !px-5 !py-3"
              style={{ background: cfg.accent }}
            >
              <Feather className="w-4 h-4" /> Add a Memorial
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/obituaries/hub"
              className="wofy-btn-ghost inline-flex items-center gap-2 !text-base"
            >
              All Obituaries
            </Link>
          </div>

          <div className="mt-8 flex items-center flex-wrap gap-x-6 gap-y-2 text-xs" style={{ color: "var(--wofy-text-soft)" }}>
            {cfg.facts.map((f) => {
              const FI = f.icon;
              return (
                <span key={f.label} className="inline-flex items-center gap-1.5">
                  <FI className="w-3.5 h-3.5" /> {f.label}
                </span>
              );
            })}
          </div>
        </div>

        <aside
          className="rounded-3xl p-8 md:p-10 flex flex-col items-start justify-between min-h-[280px]"
          style={{
            background: `linear-gradient(135deg, ${cfg.accent}12, ${cfg.accent}04)`,
            border: `1px solid ${cfg.accent}30`,
          }}
        >
          <Icon className="w-12 h-12" style={{ color: cfg.accent }} />
          <div className="mt-6">
            <div className="text-[10px] font-mono uppercase tracking-widest opacity-70">
              Currently remembered
            </div>
            <div className="font-display text-5xl md:text-6xl leading-none mt-1">
              {loading ? "—" : items.length.toLocaleString()}
            </div>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              {cfg.label.toLowerCase()} memorials on the platform. Add one to
              honour someone dear.
            </p>
          </div>
        </aside>
      </section>

      {/* Grid -------------------------------------------------------- */}
      <section className="mt-14" data-testid="category-grid">
        {loading ? (
          <div className="flex items-center gap-2 py-16 justify-center text-sm" style={{ color: "var(--wofy-text-soft)" }}>
            <Loader2 className="w-4 h-4 animate-spin" /> Loading {cfg.label.toLowerCase()} memorials…
          </div>
        ) : items.length === 0 ? (
          <div className="wofy-card text-center py-16">
            <Icon className="w-8 h-8 opacity-40 mx-auto" style={{ color: cfg.accent }} />
            <div className="font-display text-2xl mt-3">No memorials yet in this category</div>
            <p className="mt-2 text-sm" style={{ color: "var(--wofy-text-soft)" }}>
              Be the first to add one — every story matters.
            </p>
            <div className="mt-6">
              <Link
                to="/obituaries/submit"
                className="wofy-btn-primary inline-flex items-center gap-2"
                style={{ background: cfg.accent }}
              >
                <Feather className="w-4 h-4" /> Add a Memorial
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {items.map((m) => (
              <Link
                key={m.id}
                to={`/celebrate/${m.slug || m.id}`}
                data-testid={`category-card-${m.id}`}
                className="wofy-card block hover:shadow-md transition-shadow overflow-hidden group"
              >
                {m.portrait ? (
                  <img
                    src={m.portrait}
                    alt={m.full_name || "Memorial portrait"}
                    loading="lazy"
                    className="w-full aspect-square object-cover rounded-xl mb-3 group-hover:scale-[1.01] transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full aspect-square rounded-xl mb-3 bg-neutral-100 flex items-center justify-center">
                    <Icon className="w-8 h-8 opacity-30" style={{ color: cfg.accent }} />
                  </div>
                )}
                <div className="font-display text-lg leading-tight truncate">
                  {m.name_latin || m.preferred_name || m.full_name}
                </div>
                {(m.verification_badges?.length > 0) && (
                  <MemorialBadges
                    badges={m.verification_badges}
                    size="sm"
                    className="mt-1.5"
                    testidPrefix={`category-badge-${m.id}`}
                  />
                )}
                <div className="mt-1 text-xs flex items-center gap-1 flex-wrap" style={{ color: "var(--wofy-text-soft)" }}>
                  {m.age_at_death ? <span>Age {m.age_at_death}</span> : null}
                  {m.age_at_death && m.location?.city ? <span>·</span> : null}
                  {m.location?.city ? (
                    <span className="inline-flex items-center gap-0.5">
                      <MapPin className="w-3 h-3" /> {m.location.city}
                    </span>
                  ) : null}
                </div>
                {(m.title || m.occupation) && (
                  <div className="mt-2 text-[11px] font-mono uppercase tracking-wide opacity-70 truncate">
                    {m.title ? `${m.title} — ` : ""}{m.occupation || ""}
                  </div>
                )}
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Articles of Interest — populated from /api/sp-articles/by-tag */}
      <ArticlesOfInterest
        tags={cfg.article_tags || cfg.tags}
        title="Articles of Interest"
        subtitle={`Editorial reads on ${cfg.label.toLowerCase()} and community memory.`}
        accent={cfg.accent}
        limit={3}
        testidPrefix={`articles-of-interest-${(category || "").toLowerCase()}`}
      />

      <ObituariesTabs position="bottom" />
    </div>
  );
}
